import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import daeDocs from './content/daeDocs';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const Card = ({ title, children, className = '', id }) => (
  <div id={id} className={`bg-white rounded-lg shadow-sm border ${className}`}>
    {title && (
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

const Pill = ({ children, color = 'blue' }) => {
  const map = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`inline-block text-xs px-2 py-1 rounded border ${map[color] || map.blue}`}>
      {children}
    </span>
  );
};

const CodeCard = ({ title, code, lang = 'bash' }) => {
  const handleCopy = () => {
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code);
      }
    } catch {}
  };
  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden h-full flex flex-col">
      <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700">{lang}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
          >
            Copy
          </button>
        </div>
      </div>
      <pre className="flex-1 p-3 bg-gray-900 text-green-300 text-xs md:text-sm overflow-auto min-h-[14rem] max-h-[20rem]">
        <code className="font-mono">{code}</code>
      </pre>
    </div>
  );
};

const DynamicAgenticSystem = () => {
  // Inline markdown content (no network)
  const daeMd = daeDocs;

  // Lightweight markdown component mapping with Tailwind styling
  const mdComponents = {
    h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-4 mb-2" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-xl font-semibold mt-4 mb-2" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-3 mb-2" {...props} />,
    p: ({ node, ...props }) => <p className="text-sm text-gray-800 leading-6 mb-3" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc ml-6 space-y-1 text-sm text-gray-800 mb-3" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal ml-6 space-y-1 text-sm text-gray-800 mb-3" {...props} />,
    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
    a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
    code({ inline, className, children, ...props }) {
      if (inline) {
        return <code className="bg-gray-100 px-1 rounded text-[0.95em]" {...props}>{children}</code>;
      }
      return (
        <pre className="bg-gray-900 text-green-300 p-3 rounded overflow-x-auto text-xs md:text-sm mb-3">
          <code className={className} {...props}>{children}</code>
        </pre>
      );
    },
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border text-sm" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-gray-50" {...props} />,
    th: ({ node, ...props }) => <th className="border px-2 py-1 text-left font-semibold" {...props} />,
    td: ({ node, ...props }) => <td className="border px-2 py-1 align-top" {...props} />,
    hr: ({ node, ...props }) => <hr className="my-4 border-gray-200" {...props} />
  };
  const curlV1 = `curl --location 'https://api.us.inc/us/v1/code-interpreter/chat/completions' \\
  --header 'x-api-key: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "model": "asi1-mini:code_interpreter",
    "messages": [
      {
        "role": "user",
        "content": "Provide the latest news headlines from India. List the current top 10 countries by GDP in 2025."
      }
    ],
    "temperature": 0.7,
    "stream": false,
    "max_tokens": 8192
  }'`;

  const jsonBody = `{
  "model": "asi1-mini:code_interpreter",
  "messages": [
    { "role": "user", "content": "plot btc price chart" }
  ],
  "temperature": 0.7,
  "stream": true,
  "max_tokens": 8192
}`;

  const curlV2Stream = `curl --location 'https://api.us.inc/us/v2/code-interpreter/chat/completions' \\
  --header 'x-api-key: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "model": "asi1-mini:code_interpreter",
    "messages": [
      { "role": "user", "content": "Generate a line chart of Bitcoin (BTC)." }
    ],
    "temperature": 0.7,
    "stream": true,
    "max_tokens": 8192
  }'`;

  const jsNonStream = `const resp = await fetch('https://api.us.inc/us/v1/code-interpreter/chat/completions', {
  method: 'POST',
  headers: {
    'x-api-key': process.env.ASI_API_KEY,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    model: 'asi1-mini:code_interpreter',
    messages: [{ role: 'user', content: 'plot btc price chart' }],
    temperature: 0.7,
    stream: false,
    max_tokens: 8192
  })
});
const data = await resp.json();
console.log(data.choices?.[0]?.message?.content);
console.log('plans:', data.plans);
console.log('intermediate_stages:', data.intermediate_stages);`;

  const jsStream = `const resp = await fetch('https://api.us.inc/us/v2/code-interpreter/chat/completions', {
  method: 'POST',
  headers: {
    'x-api-key': process.env.ASI_API_KEY,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    model: 'asi1-mini:code_interpreter',
    messages: [{ role: 'user', content: 'Generate a line chart of Bitcoin (BTC).' }],
    temperature: 0.7,
    stream: true,
    max_tokens: 8192
  })
});
if (!resp.body) throw new Error('No response body');
const reader = resp.body.getReader();
const decoder = new TextDecoder();
let buffer = '';
while (true) {
  const { value, done } = await reader.read();
  if (done) break;
  buffer += decoder.decode(value, { stream: true });
  let lines = buffer.split('\\n');
  buffer = lines.pop() || '';
  for (const line of lines) {
    if (line.startsWith('data:')) {
      const json = line.slice(5).trim();
      if (!json) continue;
      try {
        const chunk = JSON.parse(json);
        const delta = chunk?.choices?.[0]?.delta?.content || '';
        process.stdout.write(delta);
      } catch {}
    }
  }
}`;

  const pyNonStream = `import os, requests

url = "https://api.us.inc/us/v1/code-interpreter/chat/completions"
headers = {
    "x-api-key": os.environ["ASI_API_KEY"],
    "Content-Type": "application/json"
}
payload = {
    "model": "asi1-mini:code_interpreter",
    "messages": [{"role": "user", "content": "plot btc price chart"}],
    "temperature": 0.7,
    "stream": False,
    "max_tokens": 8192
}

r = requests.post(url, headers=headers, json=payload, timeout=120)
r.raise_for_status()
data = r.json()
print(data["choices"][0]["message"]["content"])
print("plans:", data.get("plans"))
print("intermediate_stages:", data.get("intermediate_stages"))`;

  const pyStream = `import os, requests, json

url = "https://api.us.inc/us/v2/code-interpreter/chat/completions"
headers = {
    "x-api-key": os.environ["ASI_API_KEY"],
    "Content-Type": "application/json"
}
payload = {
    "model": "asi1-mini:code_interpreter",
    "messages": [{"role": "user", "content": "Generate a line chart of Bitcoin (BTC)."}],
    "temperature": 0.7,
    "stream": True,
    "max_tokens": 8192
}

with requests.post(url, headers=headers, json=payload, stream=True, timeout=300) as r:
    r.raise_for_status()
    for line in r.iter_lines(decode_unicode=True):
        if not line:
            continue
        if line.startswith("data:"):
            try:
                chunk = json.loads(line[5:].strip())
                delta = chunk.get("choices", [{}])[0].get("delta", {}).get("content", "")
                print(delta, end="", flush=True)
            except Exception:
                pass`;

  // Slug helper for anchors
  const slugify = (s) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Split markdown into cards by "## " headings
  const daeSections = useMemo(() => {
    if (!daeMd) return [];
    const lines = daeMd.split('\n');
    const sections = [];
    let currentTitle = 'Overview';
    let buffer = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('## ')) {
        if (buffer.length) {
          sections.push({ title: currentTitle, content: buffer.join('\n').trim() });
        }
        currentTitle = line.replace(/^##\s+/, '').trim();
        buffer = [];
      } else if (line.startsWith('# ')) {
        // Skip top-level H1 (we present a page-level title already)
        continue;
      } else {
        buffer.push(line);
      }
    }
    if (buffer.length) {
      sections.push({ title: currentTitle, content: buffer.join('\n').trim() });
    }
    return sections.map((s) => ({ ...s, id: slugify(s.title) }));
  }, [daeMd]);

  // Tabbed code examples component
  const CodeTabs = () => {
    const examples = [
      { id: 'curlV1', label: 'cURL — v1', code: curlV1, lang: 'bash' },
      { id: 'curlV2', label: 'cURL — v2', code: curlV2Stream, lang: 'bash' },
      { id: 'json', label: 'JSON Body', code: jsonBody, lang: 'json' },
      { id: 'js1', label: 'JavaScript — non-stream', code: jsNonStream, lang: 'javascript' },
      { id: 'js2', label: 'JavaScript — v2 streaming', code: jsStream, lang: 'javascript' },
      { id: 'py1', label: 'Python — non-stream', code: pyNonStream, lang: 'python' },
      { id: 'py2', label: 'Python — v2 streaming', code: pyStream, lang: 'python' },
    ];
    const [active, setActive] = useState(examples[0].id);
    const current = examples.find((e) => e.id === active) || examples[0];

    const handleCopy = () => {
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(current.code);
        }
      } catch {}
    };

    return (
      <div className="w-full">
        {/* Tab bar */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2 overflow-x-auto" role="tablist" aria-label="API example tabs">
            {examples.map((ex) => (
              <button
                key={ex.id}
                role="tab"
                aria-selected={active === ex.id}
                onClick={() => setActive(ex.id)}
                className={`px-3 py-2 text-xs md:text-sm rounded-t border border-b-0 ${
                  active === ex.id
                    ? 'bg-white text-blue-700 border-gray-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent'
                }`}
              >
                {ex.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700">{current.lang}</span>
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            >
              Copy
            </button>
          </div>
        </div>

        {/* Code pane */}
        <div className="border border-gray-300 rounded-b overflow-hidden bg-[#1e1e1e]">
          <div className="min-h-[18rem] max-h-[26rem] overflow-auto">
            <SyntaxHighlighter
              language={current.lang}
              style={vscDarkPlus}
              customStyle={{ padding: 12, margin: 0, background: 'transparent' }}
              wrapLongLines={false}
            >
              {current.code}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Dynamic Agent Engine — Extended Documentation */}
      <Card title="Dynamic Agent Engine — Detailed Docs">
        <div className="text-sm text-gray-600">
          Comprehensive reference rendered as cards from internal documentation.
        </div>
      </Card>
      {daeMd ? (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Sticky TOC (left column) */}
          <div className="md:col-span-4 lg:col-span-3">
            <Card title="Contents" className="md:sticky md:top-20">
              <ul className="text-sm text-gray-700 space-y-1 list-disc ml-5">
                {daeSections.map((s) => (
                  <li key={s.id}>
                    <a
                      className="text-blue-600 hover:underline cursor-pointer"
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(s.id);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Content cards (right column) */}
          <div className="md:col-span-8 lg:col-span-9 space-y-4">
            {daeSections.map((sec, idx) => (
              <div key={sec.id || sec.title + idx}>
                {sec.title === 'Overview' ? (
                  <Card id={sec.id} title={sec.title} className="bg-blue-50 border-blue-200">
                    <div className="text-[0.95rem] leading-7 text-gray-800">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {sec.content}
                      </ReactMarkdown>
                    </div>
                  </Card>
                ) : sec.title === 'Advanced Features & Capabilities' ? (
                  <Card id={sec.id} title={sec.title}>
                    {(() => {
                      const chunks = sec.content.split('\n### ').filter(Boolean);
                      if (chunks.length === 0) {
                        return (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {sec.content}
                          </ReactMarkdown>
                        );
                      }
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {chunks.map((chunk, i) => {
                            const [first, ...rest] = chunk.split('\n');
                            const subTitle = first.replace(/^#+\s*/, '').trim();
                            const body = rest.join('\n').trim();
                            return (
                              <div key={i} className="rounded-lg border bg-white p-4 shadow-sm">
                                <div className="font-semibold text-gray-900 mb-2">{subTitle}</div>
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                  {body}
                                </ReactMarkdown>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </Card>
                ) : sec.title.startsWith('How It Works') ? (
                  <Card id={sec.id} title={sec.title}>
                    {(() => {
                      const phases = sec.content.split('\n### ').filter(Boolean);
                      if (phases.length === 0) {
                        return (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {sec.content}
                          </ReactMarkdown>
                        );
                      }
                      return (
                        <div className="relative">
                          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                          <div className="space-y-6">
                            {phases.map((chunk, i) => {
                              const [first, ...rest] = chunk.split('\n');
                              const title = first.replace(/^#+\s*/, '').trim();
                              const body = rest.join('\n').trim();
                              return (
                                <div key={i} className="pl-10 relative">
                                  <div className="absolute left-3 top-1.5 h-3 w-3 rounded-full bg-blue-500 border-2 border-white shadow" />
                                  <div className="text-sm text-gray-500">Phase {i + 1}</div>
                                  <div className="font-semibold text-gray-900 mb-1">{title}</div>
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                    {body}
                                  </ReactMarkdown>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </Card>
                ) : sec.title.startsWith('Comparison with Leading AI Models') ? (
                  <Card id={sec.id} title={sec.title}>
                    {(() => {
                      const chunks = sec.content.split('\n### ').filter(Boolean);
                      const models = ['ChatGPT', 'Claude', 'Gemini'];
                      const entries = chunks
                        .map((chunk) => {
                          const [first, ...rest] = chunk.split('\n');
                          const t = first.replace(/^#+\s*/, '').trim();
                          return { title: t, body: rest.join('\n').trim() };
                        })
                        .filter((e) => models.some((m) => e.title.startsWith(m)));
                      if (entries.length === 0) {
                        return (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {sec.content}
                          </ReactMarkdown>
                        );
                      }
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {entries.map((e, i) => (
                            <div key={i} className="rounded-lg border bg-white p-4 shadow-sm">
                              <div className="font-semibold text-gray-900 mb-2">{e.title}</div>
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                {e.body}
                              </ReactMarkdown>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </Card>
                ) : (
                  <Card id={sec.id} title={sec.title}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {sec.content}
                    </ReactMarkdown>
                  </Card>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500">Loading detailed documentation…</div>
      )}
      {/* DAS Overview */}
      <Card title="Dynamic Agentic System (DAS)">
        <p className="text-sm text-gray-800 leading-6">
          Dynamic Agentic System (DAS) is the umbrella capability that plans tasks, calls tools (like web search),
          executes code for analysis and visualization, and can stream results progressively. You may see
          “code_interpreter” in the current model ID and endpoint paths for compatibility; the product should be
          referred to as the Dynamic Agentic System (DAS).
        </p>
      </Card>

      {/* Capabilities */}
      <Card title="Capabilities">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-none shadow-none">
            <div className="space-y-2">
              <Pill>Planning</Pill>
              <p className="text-sm text-gray-700">
                Returns a high-level plan as a <code className="bg-gray-100 px-1 rounded">plans</code> array.
              </p>
            </div>
          </Card>
          <Card className="border-none shadow-none">
            <div className="space-y-2">
              <Pill color="green">Tool Calls</Pill>
              <p className="text-sm text-gray-700">
                Invokes built-in tools (e.g., news search) and logs outputs in
                <code className="bg-gray-100 mx-1 px-1 rounded">intermediate_stages</code>.
              </p>
            </div>
          </Card>
          <Card className="border-none shadow-none">
            <div className="space-y-2">
              <Pill color="purple">Code Execution</Pill>
              <p className="text-sm text-gray-700">
                Secure code execution for data wrangling, tables, and chart generation.
              </p>
            </div>
          </Card>
          <Card className="border-none shadow-none">
            <div className="space-y-2">
              <Pill color="orange">Streaming (v2)</Pill>
              <p className="text-sm text-gray-700">
                SSE-style chunks (<code className="bg-gray-100 px-1 rounded">chat.completion.chunk</code>) for low-latency UX.
              </p>
            </div>
          </Card>
        </div>
      </Card>

      {/* API */}
      <Card title="API Endpoints & Auth">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">Auth Header</div>
            <div className="text-sm">
              <code className="bg-gray-100 px-1 rounded">x-api-key: YOUR_API_KEY</code>
            </div>
            <div className="text-xs text-gray-500 mt-2">Model</div>
            <div className="text-sm">
              <code className="bg-gray-100 px-1 rounded">asi1-mini:code_interpreter</code>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">v1 (single response)</div>
            <div className="text-sm break-all">
              <code className="bg-gray-100 px-1 rounded">
                https://api.us.inc/us/v1/code-interpreter/chat/completions
              </code>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">v2 (streaming)</div>
            <div className="text-sm break-all">
              <code className="bg-gray-100 px-1 rounded">
                https://api.us.inc/us/v2/code-interpreter/chat/completions
              </code>
            </div>
          </div>
        </div>
      </Card>

      {/* Schemas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Request Schema">
          <ul className="text-sm text-gray-700 space-y-2 list-disc ml-5">
            <li><code className="bg-gray-100 px-1 rounded">model</code>: "asi1-mini:code_interpreter"</li>
            <li><code className="bg-gray-100 px-1 rounded">messages</code>: array of chat messages</li>
            <li><code className="bg-gray-100 px-1 rounded">temperature</code>: number (e.g., 0.7)</li>
            <li><code className="bg-gray-100 px-1 rounded">stream</code>: boolean</li>
            <li><code className="bg-gray-100 px-1 rounded">max_tokens</code>: number (e.g., 8192)</li>
          </ul>
        </Card>
        <Card title="Response Schema (non-stream)">
          <ul className="text-sm text-gray-700 space-y-2 list-disc ml-5">
            <li>id, object, created, model</li>
            <li>choices[0].message.content</li>
            <li><code className="bg-gray-100 px-1 rounded">plans</code>: string[]</li>
            <li><code className="bg-gray-100 px-1 rounded">intermediate_stages</code>: structured snapshots</li>
          </ul>
        </Card>
      </div>

      {/* Streaming card */}
      <Card title="Streaming (v2)">
        <p className="text-sm text-gray-700">
          When <code className="bg-gray-100 px-1 rounded">stream: true</code> at the v2 endpoint, the server returns SSE-like
          <code className="bg-gray-100 mx-1 px-1 rounded">data:</code> lines. Each JSON chunk uses
          <code className="bg-gray-100 mx-1 px-1 rounded">object: "chat.completion.chunk"</code> with incremental
          <code className="bg-gray-100 mx-1 px-1 rounded">choices[0].delta.content</code>. Aggregate deltas to reconstruct the final output.
        </p>
      </Card>

      {/* Examples */}
      <Card title="API Examples">
        <CodeTabs />
      </Card>

      {/* Best Practices & Terminology */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Best Practices">
          <ul className="text-sm text-gray-700 space-y-2 list-disc ml-5">
            <li>Ask the agent to output a plan and citations to leverage <code className="bg-gray-100 px-1 rounded">plans</code> and <code className="bg-gray-100 px-1 rounded">intermediate_stages</code>.</li>
            <li>State expected artifacts (e.g., “produce a line chart”).</li>
            <li>Prefer v2 streaming for progressive UX.</li>
            <li>Handle 401, 429, and 400 with retries/backoff as appropriate.</li>
          </ul>
        </Card>
        <Card title="Terminology">
          <ul className="text-sm text-gray-700 space-y-2 list-disc ml-5">
            <li><strong>DAS</strong>: Agent runtime for planning, tool calls, and code execution.</li>
            <li><strong>Plans</strong>: High-level sequence of intended steps.</li>
            <li><strong>Intermediate stages</strong>: Structured snapshots of tool-use and partial results.</li>
          </ul>
        </Card>
      </div>


      {/* Notes */}
      <Card title="Notes">
        <p className="text-sm text-gray-800 leading-6">
          Naming: Use “Dynamic Agentic System (DAS)” in external materials. The “code_interpreter” naming in paths and
          model ID is for compatibility only. Never hardcode API keys; use environment variables or a secure secret store.
        </p>
      </Card>
    </div>
  );
};

export default DynamicAgenticSystem;
